// ---------------------------------------------------------------
// privacyNotice.js — Aviso de privacidad del portal.
//
// El texto vive aquí, versionado. Cuando cambie algo de fondo se
// sube PRIVACY_VERSION y la web vuelve a pedir la lectura a todo el
// mundo, guardando qué versión leyó cada uno y cuándo. Así siempre
// se puede demostrar qué información se le dio a cada persona.
//
// OJO: este texto lo tiene que revisar quien lleve la protección de
// datos de la farmacia. Los apartados y la estructura están, pero el
// responsable, la base jurídica y los plazos hay que confirmarlos.
// ---------------------------------------------------------------

import { abrirDocumentoAcreditativo } from "./legalDoc";

// v2: revisión de protección de datos (noviembre de 2026). Se añadieron
// las correcciones con historial, las incidencias, el directorio, la
// pérdida de acceso al dejar la farmacia, los proveedores y que los
// fichajes no se pueden borrar.
// v3: las correcciones que pide la propia persona desde su fichaje.
export const PRIVACY_VERSION = 3;

export const PRIVACY_TITLE = "Cómo tratamos tus datos";

export const PRIVACY_INTRO =
  "Este portal es la herramienta interna con la que la farmacia organiza " +
  "los turnos, el registro de jornada y las tareas del día. Aquí tienes, " +
  "en claro, qué se guarda de ti y qué puedes hacer al respecto.";

export const PRIVACY_SECTIONS = [
  {
    icon: "person",
    title: "Quién responde de tus datos",
    body:
      "Alberto Jiménez Buendía, farmacéutico titular de la Farmacia Buendía, como empresa empleadora. Para cualquier duda o para " +
      "ejercer tus derechos, habla con la dirección de la farmacia.",
  },
  {
    icon: "clipboard",
    title: "Qué se guarda",
    body:
      "Tu nombre de usuario y correo; tu horario mensual, vacaciones y " +
      "ausencias; tus fichajes de entrada y salida, las correcciones que " +
      "pidas tú y las que haga la dirección en ellos, con su motivo; las " +
      "incidencias de tu " +
      "fichaje y las explicaciones que añada la dirección; las tareas de " +
      "turno que completas; tus eventos del calendario; los cambios de turno " +
      "que pides o aceptas; y un código aleatorio del dispositivo desde el " +
      "que fichas, con el identificador de tu cuenta. No se guarda tu " +
      "dirección IP: sólo se comprueba en el momento y se descarta.",
  },
  {
    icon: "scale",
    title: "Para qué y con qué amparo",
    body:
      "Para organizar el trabajo y cumplir con la obligación legal de llevar " +
      "un registro diario de jornada (artículo 34.9 del Estatuto de los " +
      "Trabajadores). El fichaje, además, sólo se activa tras tu " +
      "consentimiento expreso, que puedes consultar y descargar en Mi perfil.",
  },
  {
    icon: "team",
    title: "Quién ve qué",
    body:
      "Tus compañeros ven tu nombre, las horas de tus turnos de cada día y si " +
      "estás de vacaciones, porque hace falta para organizarse. Tus bajas, " +
      "tus fichajes, tus incidencias y tu correo NO los ven: son cosa tuya y " +
      "de la dirección, y un día de baja les aparece igual que un día libre. " +
      "Cada vez que un administrador consulta datos concretos de una persona, " +
      "o el listado de fichajes, queda anotado en un registro de accesos que " +
      "no se puede editar y que tú puedes ver en tu descarga de datos. Sólo " +
      "se pueden borrar los apuntes de más de un año.",
  },
  {
    icon: "clock",
    title: "Cuánto tiempo",
    body:
      "Los fichajes y tu consentimiento del fichaje se conservan al menos " +
      "cuatro años, como exige la ley, y nadie puede borrarlos desde el " +
      "portal, tampoco la dirección. Al dejar la farmacia pierdes el acceso " +
      "al portal. Si se elimina tu ficha, se borran tus horarios, eventos y " +
      "el resto de datos personales; los fichajes, sus correcciones y el " +
      "consentimiento se conservan, y las tareas completadas quedan sin " +
      "nombre.",
  },
  {
    icon: "shield",
    title: "Qué puedes hacer",
    body:
      "Descargar todo lo que se guarda de ti desde Mi perfil, en un fichero, " +
      "cuando quieras: incluye las correcciones de tus fichajes y quién ha " +
      "consultado tus datos. Si un fichaje está mal, puedes pedir que lo " +
      "corrijan desde el apartado de Fichaje. También puedes pedir que se " +
      "corrija lo que esté " +
      "mal, que se limite su uso, o presentar una reclamación ante la Agencia " +
      "Española de Protección de Datos (aepd.es) si crees que algo no se está " +
      "haciendo bien.",
  },
  {
    icon: "device",
    title: "Dónde están alojados",
    body:
      "La base de datos está en Google Firebase, en servidores de la Unión " +
      "Europea (Madrid). El inicio de sesión lo gestiona Google Firebase " +
      "Authentication, y la comprobación de red del fichaje usa el servicio " +
      "externo api.ipify.org, que sólo recibe la IP de tu conexión. El acceso " +
      "está protegido por reglas que se comprueban en el servidor, no sólo en " +
      "la pantalla.",
  },
];

/**
 * Justificante de que esta persona ha leído el aviso. Ojo con el
 * lenguaje: esto NO es un consentimiento (la farmacia no pide permiso
 * para informar, informa), así que el documento dice "leído y
 * entendido", no "aceptado".
 */
export function abrirJustificantePrivacidad({ nombre, leidoEn, version }) {
  return abrirDocumentoAcreditativo({
    titulo: PRIVACY_TITLE,
    entradilla:
      "Información sobre el tratamiento de datos facilitada a la persona trabajadora.",
    intro: PRIVACY_INTRO,
    puntos: PRIVACY_SECTIONS.map((s) => ({ title: s.title, text: s.body })),
    tituloPuntos: "Información facilitada",
    declaracion: null,
    etiquetaFirma: "Leído y entendido por",
    nombre,
    fecha: leidoEn,
    version: version || PRIVACY_VERSION,
    notaFirma:
      "Lectura confirmada de forma telemática pulsando \"Entendido, " +
      "continuar\" al entrar en el portal. La fecha y la hora proceden del " +
      "registro guardado en el servidor en ese mismo momento.",
  });
}
